import { Permission } from '@towns-protocol/web3'
import { Box, Icon, Stack, Text, Toggle } from '@ui'

const HIDE_PERMISSIONS = [
    Permission.Undefined,
    // Bots by their nature won't work in a space unless they get installed.
    // Thats why they don't need the `JoinSpace` permission.
    Permission.JoinSpace,
    // As of 2025-09-22, we don't have a invite feature yet
    Permission.Invite,
] satisfies Permission[]

const filterBotPermissions = (permissions: Permission[]) =>
    permissions.filter((p) => !HIDE_PERMISSIONS.includes(p as (typeof HIDE_PERMISSIONS)[number]))

const BOT_PERMISSION_DISPLAYNAME = {
    [Permission.Read]: 'Read Messages',
    [Permission.Write]: 'Send Messages',
    [Permission.React]: 'React to Messages',
    [Permission.Redact]: 'Delete Messages',
    [Permission.ModifyBanning]: 'Ban Members',
    [Permission.PinMessage]: 'Pin Messages',
    [Permission.AddRemoveChannels]: 'Manage Channels',
    [Permission.ModifySpaceSettings]: 'Manage Roles & Permissions',
} satisfies Record<Exclude<Permission, (typeof HIDE_PERMISSIONS)[number]>, string>

const BOT_PERMISSION_DESCRIPTION = {
    [Permission.Read]: 'Allow the bot to read messages.',
    [Permission.Write]: 'Allow the bot to send messages.',
    [Permission.React]: 'Allow the bot to react to messages.',
    [Permission.Redact]: 'Allow the bot to delete messages.',
    [Permission.ModifyBanning]: 'Allow the bot to ban members.',
    [Permission.PinMessage]: 'Allow the bot to pin messages.',
    [Permission.AddRemoveChannels]: 'Allow the bot to create and manage channels.',
    [Permission.ModifySpaceSettings]: 'Allow the bot to create and manage roles & permissions.',
}

interface PermissionsDisplayProps {
    permissions: Permission[]
    mode?: 'view' | 'edit'
    selectedPermissions?: Permission[]
    onTogglePermission?: (permission: Permission) => void
}

export const PermissionsDisplay = ({
    permissions,
    mode = 'view',
    selectedPermissions,
    onTogglePermission,
}: PermissionsDisplayProps) => {
    const isViewMode = mode === 'view'

    return (
        <Stack gap>
            {filterBotPermissions(permissions).map((permission) => (
                <Stack
                    horizontal
                    alignItems="center"
                    justifyContent="spaceBetween"
                    key={permission}
                    paddingY="sm"
                >
                    <Stack gap horizontal alignItems="center">
                        {isViewMode && (
                            <Box
                                centerContent
                                width="x2"
                                height="x2"
                                rounded="full"
                                background="cta1"
                            >
                                <Icon type="check" size="square_xs" />
                            </Box>
                        )}
                        <Stack gap="sm">
                            <Text fontWeight="medium">
                                {
                                    BOT_PERMISSION_DISPLAYNAME[
                                        permission as keyof typeof BOT_PERMISSION_DISPLAYNAME
                                    ]
                                }
                            </Text>
                            <Text size="sm" color="gray2">
                                {
                                    BOT_PERMISSION_DESCRIPTION[
                                        permission as keyof typeof BOT_PERMISSION_DESCRIPTION
                                    ]
                                }
                            </Text>
                        </Stack>
                    </Stack>
                    {!isViewMode && onTogglePermission && (
                        <Toggle
                            toggled={selectedPermissions?.includes(permission) ?? false}
                            onToggle={() => onTogglePermission(permission)}
                        />
                    )}
                </Stack>
            ))}
        </Stack>
    )
}
